/**
 * <ReplyForm />
 *
 * Unified reply + reply-to-sell form. A single textarea for the reply
 * copy plus a big prominent toggle: "I have this part for sale."
 * When enabled, reveals the product fields (title, price, part_number,
 * condition, shipping type, images). Posts to:
 *     POST /api/threads/{thread_id}/replies
 *
 * Behavioral notes:
 *   * Fully controlled -- no uncontrolled DOM reads.
 *   * Image uploads are stubbed as URL input (comma-separated) until our
 *     object-storage endpoint lands in a later phase; marked clearly in UI.
 *   * Loading state disables the submit button + shows a spinner inside it.
 *   * Errors surface in a red inline banner (role="alert" so screen readers
 *     announce them).
 */
import * as React from "react";
import { api, extractApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import type { ProductCondition, ShippingType } from "../../lib/types";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import { ImageUploader } from "../ui/ImageUploader";
import { cn } from "../../lib/cn";

interface Props {
  threadId: string;
  /** Called after a successful post so the parent can re-fetch the thread. */
  onPosted: () => void;
}

export function ReplyForm({ threadId, onPosted }: Props) {
  const { user } = useAuth();

  const [body, setBody] = React.useState("");
  const [forSale, setForSale] = React.useState(false);
  const [title, setTitle] = React.useState("");
  const [price, setPrice] = React.useState("");
  const [partNumber, setPartNumber] = React.useState("");
  const [condition, setCondition] = React.useState<ProductCondition>("USED");
  const [shippingType, setShippingType] = React.useState<ShippingType>("BUYER_PAYS");
  const [shippingCost, setShippingCost] = React.useState("");
  const [images, setImages] = React.useState<string[]>([]);

  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  if (!user) {
    // Not logged in -- render a friendly call to action instead of the form.
    return (
      <div
        data-testid="reply-form-logged-out"
        className="rounded-2xl border border-surface-border bg-surface-raised p-6 text-center"
      >
        <p className="text-ink font-semibold mb-3">
          Sign in to reply or list a part.
        </p>
        <a
          href="/login"
          className="inline-flex items-center font-semibold text-brand hover:text-brand-hover"
        >
          Sign in →
        </a>
      </div>
    );
  }

  const reset = () => {
    setBody("");
    setForSale(false);
    setTitle("");
    setPrice("");
    setPartNumber("");
    setCondition("USED");
    setShippingType("BUYER_PAYS");
    setShippingCost("");
    setImages([]);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const payload: Record<string, unknown> = { body: body.trim() };

    if (forSale) {
      const priceNum = Number(price);
      const shipNum = Number(shippingCost || "0");
      if (!title.trim() || !partNumber.trim() || !Number.isFinite(priceNum) || priceNum < 0) {
        setError("Fill in title, part number, and a valid price to list a part.");
        setLoading(false);
        return;
      }
      if (images.length === 0) {
        setError("Please add at least one photo to list a part.");
        setLoading(false);
        return;
      }
      const images_list = images;
      payload.new_product = {
        title: title.trim(),
        description: body.trim() || title.trim(),
        price: priceNum,
        part_number: partNumber.trim(),
        shipping_type: shippingType,
        shipping_cost: shippingType === "FREE" ? 0 : shipNum,
        condition,
        images: images_list,
      };
    }

    try {
      await api.post(`/threads/${threadId}/replies`, payload);
      reset();
      onPosted();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="reply-form"
      className="rounded-2xl border border-surface-border bg-white p-5 md:p-6"
      aria-label="Reply to this thread"
    >
      <h3 className="text-xl font-bold mb-3">Add your reply</h3>

      <label
        htmlFor="reply-body"
        className="mb-1.5 block text-sm font-semibold text-ink"
      >
        Your reply
      </label>
      <textarea
        id="reply-body"
        data-testid="reply-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        required
        rows={5}
        placeholder="Share what you know, or describe the part you’re listing..."
        className="w-full rounded-xl border-2 border-surface-border bg-white px-3 py-3 text-base placeholder:text-ink-subtle focus:outline-none focus:border-ink"
      />

      {/* Reply-to-sell toggle */}
      <label
        data-testid="for-sale-toggle-wrap"
        className={cn(
          "mt-4 flex items-center gap-4 rounded-xl border-2 p-4 cursor-pointer transition-colors",
          forSale
            ? "border-brand bg-brand-softer"
            : "border-surface-border hover:border-ink",
        )}
      >
        <input
          type="checkbox"
          data-testid="for-sale-toggle"
          checked={forSale}
          onChange={(e) => setForSale(e.target.checked)}
          className="h-6 w-6 accent-brand"
        />
        <div className="flex-1">
          <div className="font-bold text-ink">I have this part for sale</div>
          <div className="text-sm text-ink-muted">
            Attach a listing to your reply — buyers can purchase it in one tap.
          </div>
        </div>
      </label>

      {/* Product fields */}
      {forSale && (
        <div
          data-testid="for-sale-fields"
          className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in"
        >
          <div className="md:col-span-2">
            <Input
              label="Listing title"
              placeholder="e.g. OEM fuel pump, Yamaha F250 2018"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required={forSale}
              data-testid="sell-title"
            />
          </div>
          <Input
            label="Part number"
            placeholder="OEM / manufacturer PN"
            value={partNumber}
            onChange={(e) => setPartNumber(e.target.value)}
            required={forSale}
            data-testid="sell-partnum"
          />
          <Input
            label="Price (USD)"
            type="number"
            min="0"
            step="0.01"
            placeholder="89.00"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            required={forSale}
            data-testid="sell-price"
          />
          <Select
            label="Condition"
            value={condition}
            onChange={(e) => setCondition(e.target.value as ProductCondition)}
            options={[
              { value: "NEW", label: "New" },
              { value: "USED", label: "Used" },
              { value: "REFURBISHED", label: "Refurbished" },
            ]}
            data-testid="sell-condition"
          />
          <Select
            label="Shipping"
            value={shippingType}
            onChange={(e) => setShippingType(e.target.value as ShippingType)}
            options={[
              { value: "FREE", label: "Free shipping (I pay)" },
              { value: "BUYER_PAYS", label: "Buyer pays shipping" },
            ]}
            data-testid="sell-shipping-type"
          />
          {shippingType === "BUYER_PAYS" && (
            <Input
              label="Shipping cost (USD)"
              type="number"
              min="0"
              step="0.01"
              placeholder="10.00"
              value={shippingCost}
              onChange={(e) => setShippingCost(e.target.value)}
              data-testid="sell-shipping-cost"
            />
          )}
          <div className="md:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-ink">
              Photos (at least 1 required)
            </label>
            <ImageUploader
              value={images}
              onChange={setImages}
              max={5}
              testid="sell-images"
            />
          </div>
        </div>
      )}

      {error && (
        <div
          role="alert"
          data-testid="reply-form-error"
          className="mt-4 rounded-xl border-2 border-brand bg-brand-softer text-brand-ink px-3 py-2 text-sm font-medium"
        >
          {error}
        </div>
      )}

      <div className="mt-5 flex items-center justify-between gap-3">
        <span className="text-sm text-ink-muted hidden md:inline">
          Posting as <strong>@{user.username}</strong>
        </span>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={loading}
          data-testid="reply-submit"
        >
          {forSale ? "Post reply & list part" : "Post reply"}
        </Button>
      </div>
    </form>
  );
}
